import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/constants/testIds";
import { GALLERY_IMAGES, WEDDING } from "@/data/wedding";
import { useLang } from "@/i18n/LanguageContext";
import { HydrangeaFlourish, OrchidFlourish } from "@/components/wedding/Flourish";
import { Reveal } from "@/components/motion/Reveal";

export const Gallery = () => {
  const { t } = useLang();
  const heading = t("gallery.heading");
  const tag = t("gallery.tag");
  const [openIdx, setOpenIdx] = useState(-1);
  const open = openIdx >= 0;

  const next = () => setOpenIdx((i) => (i + 1) % GALLERY_IMAGES.length);
  const prev = () => setOpenIdx((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIdx(-1);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section id="gallery" data-testid={GALLERY.section} className="relative py-24 sm:py-32 bg-[color:var(--rose-50)] overflow-hidden">
      <HydrangeaFlourish
        className="absolute -top-6 -left-6 w-[100px] sm:w-[140px] md:w-[180px]"
        opacity={0.85}
      />
      <OrchidFlourish
        className="absolute -bottom-8 -right-6 w-[100px] sm:w-[140px] md:w-[180px] scale-x-[-1]"
        opacity={0.8}
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter font-light">
                {heading[0]}
                <em className="italic">{heading[1]}</em>
                {heading[2]}
              </h2>
            </div>
            <div className="hidden sm:block font-serif italic text-lg text-black/60">{WEDDING.hashtag}</div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          {GALLERY_IMAGES.map((img, i) => (
            <Reveal key={i} delay={i * 0.08} as="figure" className={`relative overflow-hidden bg-black/5 group cursor-zoom-in ${img.span}`}>
              <button
                type="button"
                data-testid={GALLERY.image(i)}
                aria-label={`Open photo ${i + 1}`}
                onClick={() => setOpenIdx(i)}
                className="block w-full h-full"
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="img-art absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <span className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                {!img.whiteBg && (
                  <span
                    data-testid={`gallery-photo-credit-${i}`}
                    className="pointer-events-none absolute bottom-2 right-2 sm:bottom-3 sm:right-3 font-serif italic text-[10px] sm:text-[11px] text-white/90 [text-shadow:0_1px_6px_rgba(0,0,0,0.5)]"
                  >
                    @chlaaneste
                  </span>
                )}
              </button>
            </Reveal>
          ))}
        </div>

        {GALLERY_IMAGES.length === 0 && (
          <Reveal>
            <div className="border border-black/10 bg-white/50 backdrop-blur-sm py-20 sm:py-28 text-center px-6">
              <div className="font-overline text-[10px] tracking-[0.3em] text-[color:var(--sunset-600)]">
                {t("gallery.soonEyebrow")}
              </div>
              <p className="mt-6 font-display italic text-3xl sm:text-4xl md:text-5xl tracking-tighter font-light text-black/80 max-w-2xl mx-auto leading-[1.15]">
                {t("gallery.soonTitle")}
              </p>
              <p className="mt-4 font-serif italic text-base sm:text-lg text-black/55 max-w-md mx-auto">
                {t("gallery.soonBody")}
              </p>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <p className="mt-10 font-serif italic text-lg text-black/60 text-center">
            {tag[0]}<span className="text-[color:var(--sunset-600)]">{WEDDING.hashtag}</span>{tag[1]}
          </p>
        </Reveal>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="lb"
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10 sm:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setOpenIdx(-1)}
          >
            <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

            <button
              type="button"
              data-testid="gallery-lightbox-close"
              aria-label="Close"
              onClick={(e) => { e.stopPropagation(); setOpenIdx(-1); }}
              className="absolute top-5 right-5 sm:top-8 sm:right-8 z-10 w-11 h-11 flex items-center justify-center border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
            >
              <X size={18} />
            </button>

            {GALLERY_IMAGES.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-3 sm:left-8 z-10 w-11 h-11 flex items-center justify-center border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-3 sm:right-8 z-10 w-11 h-11 flex items-center justify-center border border-white/25 text-white hover:bg-white hover:text-black transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            <motion.figure
              key={openIdx}
              className="relative max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY_IMAGES[openIdx].url}
                alt={GALLERY_IMAGES[openIdx].alt}
                className="max-h-[80vh] w-auto max-w-full object-contain shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
              />
              <figcaption className="mt-4 font-serif italic text-sm sm:text-base text-white/70 text-center">
                {openIdx + 1} / {GALLERY_IMAGES.length}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
